import { useState } from "react";
import { CaretDown, PlayCircle } from "@phosphor-icons/react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { cn } from "@/lib/utils";
import { LegalLinks } from "@/components/shared/LegalLinks";
import {
  CATEGORY_META,
  CATEGORY_ORDER,
  lessons,
  type Lesson,
} from "@/features/ajuda/data/lessons";

/**
 * Click-to-play facade. Shows our own YouTube thumbnail (no title) with a play
 * button; only loads the iframe (autoplay) after the user clicks. This hides
 * YouTube's pre-play title overlay for every lesson and avoids loading the
 * heavy YouTube player until it's actually wanted.
 */
function LessonVideo({ lesson }: { lesson: Lesson }) {
  const [playing, setPlaying] = useState(false);
  const id = lesson.youtubeId as string;

  if (playing) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&autoplay=1`}
        title={lesson.title}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="group relative block w-full h-full"
      aria-label={`Reproduzir vídeo: ${lesson.title}`}
    >
      <img
        src={`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`}
        onError={(e) => {
          const img = e.currentTarget as HTMLImageElement;
          if (!img.dataset.fallback) {
            img.dataset.fallback = "1";
            img.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
          }
        }}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="w-full h-full object-cover"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/35">
        <PlayCircle weight="fill" className="w-16 h-16 text-white/90 drop-shadow-lg" />
      </span>
    </button>
  );
}

function LessonRow({
  lesson,
  open,
  onToggle,
}: {
  lesson: Lesson;
  open: boolean;
  onToggle: () => void;
}) {
  const hasVideo = !!lesson.youtubeId;
  return (
    <div
      className={cn(
        "border-b border-border/60 transition-colors",
        open && "bg-card/40",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={!hasVideo}
        className={cn(
          "w-full flex items-start gap-4 py-5 px-1 text-left",
          "hover:bg-card/30 transition-colors rounded-md",
          !hasVideo && "opacity-60 cursor-not-allowed",
        )}
        aria-expanded={open}
        aria-controls={`${lesson.id}-content`}
      >
        <span className="text-xs font-mono text-muted-foreground tabular-nums pt-1 min-w-[28px]">
          {String(lesson.number).padStart(2, "0")}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-medium text-foreground">
              {lesson.title}
            </h3>
            {lesson.duration && hasVideo && (
              <span className="text-[11px] text-muted-foreground font-mono">
                · {lesson.duration}
              </span>
            )}
            {!hasVideo && (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-full px-2 py-0.5">
                Em breve
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            {lesson.description}
          </p>
        </div>
        {hasVideo && (
          <CaretDown
            weight="regular"
            className={cn(
              "w-5 h-5 text-muted-foreground transition-transform shrink-0 mt-1",
              open && "rotate-180 text-foreground",
            )}
          />
        )}
      </button>

      {open && hasVideo && (
        <div id={`${lesson.id}-content`} className="px-1 pb-6 pt-1">
          <div className="rounded-lg overflow-hidden border border-border bg-black aspect-video max-w-3xl">
            <LessonVideo lesson={lesson} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function AjudaPage() {
  usePageTitle("Ajuda");
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PlayCircle weight="duotone" className="w-6 h-6 text-primary" />
            <span className="text-sm font-medium tracking-tight">
              Código do Poder · Guia
            </span>
          </div>
          <a
            href="/"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Voltar ao app →
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.18em] text-primary mb-3">
            Guia de uso
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-4">
            Aprenda o CDP em poucos minutos
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed max-w-2xl">
            Apresentações curtas guiando você por cada tela e ferramenta. Veja
            na ordem que precisar — clique para expandir e assistir.
          </p>
        </div>

        {CATEGORY_ORDER.map((cat) => {
          const items = lessons.filter((l) => l.category === cat);
          if (items.length === 0) return null;
          const meta = CATEGORY_META[cat];
          return (
            <section key={cat} className="mb-10">
              <div className="mb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                  {meta.label}
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {meta.subtitle}
                </p>
              </div>
              <div className="border-t border-border/60">
                {items.map((lesson) => (
                  <LessonRow
                    key={lesson.id}
                    lesson={lesson}
                    open={openId === lesson.id}
                    onToggle={() =>
                      setOpenId(openId === lesson.id ? null : lesson.id)
                    }
                  />
                ))}
              </div>
            </section>
          );
        })}

      </main>
      <footer className="border-t border-border/60 px-6 py-6">
        <LegalLinks />
      </footer>
    </div>
  );
}
